import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

const atharRailQueues = new WeakMap();

function animateAtharRailScroll(rail, from, to) {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    rail.scrollLeft = to;
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const duration = 360;
    const startTime = window.performance.now();

    function step(now) {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 4);
      rail.scrollLeft = from + ((to - from) * eased);

      if (progress < 1) {
        window.requestAnimationFrame(step);
      } else {
        rail.scrollLeft = to;
        resolve();
      }
    }

    window.requestAnimationFrame(step);
  });
}

async function rotateAtharInfiniteRail(rail, direction) {
  const cards = Array.from(rail.querySelectorAll(':scope > [data-athar-rail-card]'));
  if (cards.length < 2) return;

  const card = cards[0];
  const gap = Number.parseFloat(window.getComputedStyle(rail).gap) || 0;
  const distance = card.getBoundingClientRect().width + gap;
  const initialScroll = rail.scrollLeft;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (reducedMotion) {
    if (direction < 0) {
      rail.prepend(cards[cards.length - 1]);
    } else {
      rail.append(cards[0]);
    }
    rail.scrollLeft = initialScroll;
    return;
  }

  const wrappedCard = direction < 0 ? cards[cards.length - 1] : cards[0];
  const clone = wrappedCard.cloneNode(true);
  clone.dataset.atharRailClone = 'true';
  clone.setAttribute('aria-hidden', 'true');
  clone.setAttribute('inert', '');
  clone.removeAttribute('id');

  const spacer = document.createElement('span');
  spacer.setAttribute('aria-hidden', 'true');
  spacer.style.flex = '0 0 0';

  const previousSnapType = rail.style.scrollSnapType;
  rail.style.scrollSnapType = 'none';

  try {
    if (direction < 0) {
      rail.prepend(clone);
      rail.append(spacer);
      const shortfall = Math.max(0, initialScroll + distance - (rail.scrollWidth - rail.clientWidth));
      spacer.style.flexBasis = `${shortfall}px`;
      rail.scrollLeft = initialScroll + distance;
      await animateAtharRailScroll(rail, initialScroll + distance, initialScroll);
      wrappedCard.remove();
      clone.replaceWith(wrappedCard);
    } else {
      rail.append(clone);
      rail.append(spacer);
      const shortfall = Math.max(0, initialScroll + distance - (rail.scrollWidth - rail.clientWidth));
      spacer.style.flexBasis = `${shortfall}px`;
      await animateAtharRailScroll(rail, initialScroll, initialScroll + distance);
      wrappedCard.remove();
      clone.replaceWith(wrappedCard);
    }
  } finally {
    rail.scrollLeft = initialScroll;
    rail.style.scrollSnapType = previousSnapType;
    clone.remove();
    spacer.remove();
  }
}

function initializeAtharRailDragging(rail) {
  if (rail.dataset.atharRailDragReady === 'true') return;

  rail.dataset.atharRailDragReady = 'true';
  const state = {
    active: false,
    settling: false,
    moved: false,
    suppressClick: false,
    pointerId: null,
    startX: 0,
    lastX: 0,
    initialScroll: 0,
    baseScroll: 0,
    distance: 0,
    previousSnapType: '',
    firstClone: null,
    lastClone: null,
    cards: [],
  };

  function makeClone(card) {
    const clone = card.cloneNode(true);
    clone.dataset.atharRailClone = 'true';
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('id');
    return clone;
  }

  async function finishDrag(event, cancelled = false) {
    if (!state.active || event.pointerId !== state.pointerId) return;

    state.active = false;
    state.settling = true;
    state.suppressClick = state.moved;
    if (state.suppressClick) {
      window.setTimeout(() => {
        state.suppressClick = false;
      }, 0);
    }
    rail.dataset.atharRailDragging = 'false';
    rail.dataset.atharRailSettling = 'true';
    rail.classList.remove('is-dragging');

    if (rail.hasPointerCapture(event.pointerId)) {
      rail.releasePointerCapture(event.pointerId);
    }

    const dragDistance = state.lastX - state.startX;
    const threshold = Math.min(72, state.distance * 0.18);
    let direction = 0;

    if (!cancelled && state.moved) {
      if (dragDistance > threshold) direction = -1;
      if (dragDistance < -threshold) direction = 1;
    }

    const targetScroll = state.baseScroll + (direction * state.distance);
    await animateAtharRailScroll(rail, rail.scrollLeft, targetScroll);

    state.firstClone.remove();
    state.lastClone.remove();

    if (direction < 0) rail.prepend(state.cards[state.cards.length - 1]);
    if (direction > 0) rail.append(state.cards[0]);

    rail.scrollLeft = state.initialScroll;
    rail.style.scrollSnapType = state.previousSnapType;
    rail.dataset.atharRailSettling = 'false';
    state.settling = false;
    state.firstClone = null;
    state.lastClone = null;
    state.cards = [];
  }

  rail.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'touch' && rail.matches('.athar-home__product-rail, .athar-about__icon-rail')) return;
    if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0) || state.active || state.settling) return;

    const cards = Array.from(rail.querySelectorAll(':scope > [data-athar-rail-card]:not([data-athar-rail-clone])'));
    if (cards.length < 2) return;

    const gap = Number.parseFloat(window.getComputedStyle(rail).gap) || 0;
    state.active = true;
    state.moved = false;
    state.pointerId = event.pointerId;
    state.startX = event.clientX;
    state.lastX = event.clientX;
    state.initialScroll = rail.scrollLeft;
    state.distance = cards[0].getBoundingClientRect().width + gap;
    state.previousSnapType = rail.style.scrollSnapType;
    state.cards = cards;
    state.firstClone = makeClone(cards[0]);
    state.lastClone = makeClone(cards[cards.length - 1]);

    rail.style.scrollSnapType = 'none';
    rail.prepend(state.lastClone);
    rail.append(state.firstClone);
    state.baseScroll = state.initialScroll + state.distance;
    rail.scrollLeft = state.baseScroll;
    rail.dataset.atharRailDragging = 'true';
    rail.classList.add('is-dragging');
  });

  rail.addEventListener('pointermove', (event) => {
    if (!state.active || event.pointerId !== state.pointerId) return;

    state.lastX = event.clientX;
    const dragDistance = state.lastX - state.startX;
    if (Math.abs(dragDistance) > 5 && !state.moved) {
      state.moved = true;
      rail.setPointerCapture(event.pointerId);
    }
    rail.scrollLeft = state.baseScroll - dragDistance;
    event.preventDefault();
  });

  rail.addEventListener('pointerup', (event) => {
    finishDrag(event);
  });

  rail.addEventListener('pointercancel', (event) => {
    finishDrag(event, true);
  });

  rail.addEventListener('dragstart', (event) => {
    event.preventDefault();
  });

  rail.addEventListener('click', (event) => {
    if (!state.suppressClick) return;
    state.suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  }, true);
}

function initializeAtharRails(root = document) {
  root.querySelectorAll('.athar-home__product-rail').forEach((rail) => {
    window.requestAnimationFrame(() => { rail.scrollLeft = 0; });
  });

  root.querySelectorAll('[data-athar-rail-control]').forEach((control) => {
    if (control.dataset.atharRailReady === 'true') return;

    control.dataset.atharRailReady = 'true';
    control.addEventListener('click', () => {
      const rail = document.getElementById(control.dataset.atharRailControl);
      if (!rail) return;
      if (rail.dataset.atharRailDragging === 'true' || rail.dataset.atharRailSettling === 'true') return;

      const direction = control.dataset.direction === 'previous' ? -1 : 1;
      if (rail.hasAttribute('data-athar-infinite-rail')) {
        const currentQueue = atharRailQueues.get(rail) || Promise.resolve();
        const nextQueue = currentQueue.then(() => rotateAtharInfiniteRail(rail, direction));
        atharRailQueues.set(rail, nextQueue.catch(() => {}));
        return;
      }

      const card = rail.querySelector('[data-athar-rail-card]');
      const gap = Number.parseFloat(window.getComputedStyle(rail).gap) || 32;
      const distance = card ? card.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;

      rail.scrollBy({ left: direction * distance, behavior: 'smooth' });
    });
  });

  root.querySelectorAll('[data-athar-infinite-rail]').forEach((rail) => {
    initializeAtharRailDragging(rail);
  });
}

function initializeAtharQuickAdd(root = document) {
  root.querySelectorAll('[data-athar-quick-add-form]').forEach((form) => {
    if (form.dataset.atharQuickAddReady === 'true') return;

    form.dataset.atharQuickAddReady = 'true';
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      const button = form.querySelector('button');
      const home = form.closest('.athar-home');
      const toast = home?.querySelector('[data-athar-home-cart-toast]');
      if (!button) return;

      const formData = new FormData(form);
      const merchandiseId = String(formData.get('id') || '');
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component[data-section-id]'))
        .map((component) => component.dataset.sectionId)
        .filter(Boolean);
      if (sectionIds.length) formData.set('sections', sectionIds.join(','));

      const deferredEvent = CartLinesUpdateEvent.createPromise();
      form.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'quick-add',
        lines: [{ merchandiseId, quantity: 1 }],
        promise: deferredEvent.promise,
      }));

      button.disabled = true;
      form.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(window.Theme?.routes?.cart_add_url || `${form.action}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: formData,
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || 'Could not add this product to cart.');

        const cartUrl = window.Theme?.routes?.cart_url || '/cart';
        const cartResponse = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!cartResponse.ok) throw new Error('The item was added, but the cart could not be refreshed.');
        const ajaxCart = await cartResponse.json();
        deferredEvent.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
          detail: { didError: false, itemCount: 1, items: ajaxCart.items, sections: data.sections, source: 'athar-home' },
        });

        if (toast) {
          toast.textContent = 'Added to cart.';
          toast.hidden = false;
          window.setTimeout(() => { toast.hidden = true; }, 3500);
        }
        await new Promise((resolve) => window.requestAnimationFrame(resolve));
        const cartDrawer = document.querySelector('theme-drawer#cart-drawer');
        if (cartDrawer && typeof cartDrawer.open === 'function') cartDrawer.open();
      } catch (error) {
        deferredEvent.reject(error);
        form.dispatchEvent(new CartErrorEvent({ error: error.message || 'Could not add this product to cart.', code: 'INVALID' }));
        if (toast) {
          toast.textContent = error.message || 'Could not add this product to cart.';
          toast.hidden = false;
        }
      } finally {
        button.disabled = false;
        form.removeAttribute('aria-busy');
      }
    });
  });
}

document.addEventListener('DOMContentLoaded', () => {
  initializeAtharRails();
  initializeAtharQuickAdd();
});
document.addEventListener('shopify:section:load', (event) => {
  initializeAtharRails(event.target);
  initializeAtharQuickAdd(event.target);
});
